/**
 * SegmentVerifier.jsx
 * 
 * This component provides a UI for verifying the fairness of completed segments
 * in the virtual deck system. It allows users to see the seed and salt for
 * completed segments and verify that the prizes were distributed fairly.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Button,
  Alert,
  AlertIcon,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Code,
  useToast
} from '@chakra-ui/react';
import { getBatch, getSegment } from './SegmentManager';
import { verifySegment } from './VirtualDeckGenerator';

/**
 * SegmentVerifier Component
 * 
 * Displays verification data for a completed segment and allows users to
 * verify the fairness of the segment.
 * 
 * @param {Object} props - Component props
 * @param {string} props.batchId - The batch ID
 * @param {string} props.segmentId - The segment ID
 */
const SegmentVerifier = ({ batchId, segmentId }) => {
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [segment, setSegment] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const toast = useToast();
  
  useEffect(() => {
    if (batchId && segmentId) {
      loadBatchAndSegment();
    }
  }, [batchId, segmentId]);
  
  const loadBatchAndSegment = () => {
    setLoading(true);
    
    try {
      const batchData = getBatch(batchId);
      setBatch(batchData);
      
      const segmentData = getSegment(segmentId);
      setSegment(segmentData);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading batch and segment:', error);
      toast({
        title: "Error",
        description: "Failed to load verification data.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      setLoading(false);
    }
  };
  
  const handleVerify = () => {
    if (!segment || !segment.isComplete) {
      setVerificationResult({
        isValid: false,
        message: 'This segment is not yet complete. Verification will be available when all cards are minted.'
      });
      return;
    }
    
    setLoading(true);
    
    try {
      // Verify the segment
      const isValid = verifySegment(
        segment.prizeList,
        segment.seed,
        segment.salt,
        segment.segmentHash
      );
      
      setVerificationResult({
        isValid,
        message: isValid
          ? 'Verification successful! This segment is provably fair.'
          : 'Verification failed. The segment may have been tampered with.'
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error verifying segment:', error);
      setVerificationResult({
        isValid: false,
        message: `Verification error: ${error.message}`
      });
      setLoading(false);
    }
  };
  
  if (!batch || !segment) {
    return (
      <Box p={4} borderRadius="md" bg="gray.100">
        <Heading size="md" mb={4}>Segment Verification</Heading>
        {loading ? (
          <Spinner />
        ) : (
          <Alert status="warning">
            <AlertIcon />
            Batch or segment not found
          </Alert>
        )}
      </Box>
    );
  }
  
  return (
    <Box p={4} borderRadius="md" bg="gray.100">
      <Heading size="md" mb={4}>Segment Verification</Heading>
      
      <VStack align="stretch" spacing={4}>
        <Box>
          <Heading size="sm" mb={2}>Batch Information</Heading>
          <Text>Batch ID: {batch.id}</Text>
          <Text>Game: {batch.gameName || batch.gameId}</Text>
          <Text>Created: {new Date(batch.createdAt).toLocaleString()}</Text>
        </Box>
        
        <Box>
          <Heading size="sm" mb={2}>Segment Information</Heading>
          <Text>Segment ID: {segment.id}</Text>
          <Text>Cards: {segment.nextCardIndex} / {segment.cardCount}</Text>
          <Text>Status: {segment.isComplete ? (
            <Badge colorScheme="green">Complete</Badge>
          ) : (
            <Badge colorScheme="blue">Active</Badge>
          )}</Text>
        </Box>
        
        {segment.isComplete && (
          <Box>
            <Heading size="sm" mb={2}>Verification Data</Heading>
            <Text>Seed: <Code>{segment.seed}</Code></Text>
            <Text>Salt: <Code>{segment.salt}</Code></Text>
            <Text>Segment Hash:</Text>
            <Code p={2} borderRadius="md" fontSize="sm" width="100%" overflowX="auto">
              {segment.segmentHash}
            </Code>
          </Box>
        )}
        
        <Button
          colorScheme="blue"
          onClick={handleVerify}
          isLoading={loading}
          isDisabled={!segment.isComplete}
        >
          Verify Segment
        </Button>
        
        {verificationResult && (
          <Alert
            status={verificationResult.isValid ? 'success' : 'error'}
            borderRadius="md"
          >
            <AlertIcon />
            {verificationResult.message}
          </Alert>
        )}
        
        {segment.isComplete && (
          <Box>
            <Heading size="sm" mb={2}>Prize Distribution</Heading>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Prize</Th>
                  <Th isNumeric>Count</Th>
                </Tr>
              </Thead>
              <Tbody>
                {Object.entries(segment.prizeDistribution || {}).map(([prize, count]) => (
                  <Tr key={prize}>
                    <Td>{prize}</Td>
                    <Td isNumeric>{count}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default SegmentVerifier;
